import * as ex from 'excalibur';
import { ActionType, Allegiance, ControlType } from '../types/enums';
import { Action } from '../types/interfaces';
import { TILE_SIZE } from '../config/constants';
import { Unit } from '../core/unit';
import { Grid } from '../core/grid';
import { Actor } from '../core/actor';
import { PlayerUnit } from './playerUnit';

// Dog class that stays near allies and has a bark ability that pushes enemies away
export class Dog extends Unit {
  constructor(options: {
    gridX: number,
    gridY: number,
    allegiance: Allegiance,
    initiative: number,
    controlType?: ControlType,
    grid: Grid
  }) {
    super({
      pos: new ex.Vector(options.gridX * TILE_SIZE + TILE_SIZE / 2, options.gridY * TILE_SIZE + TILE_SIZE / 2),
      gridX: options.gridX,
      gridY: options.gridY,
      type: 'dog',
      name: 'Dog',
      width: TILE_SIZE * 0.6,
      height: TILE_SIZE * 0.6,
      color: options.allegiance === Allegiance.Player ? ex.Color.fromHex('#8B4513') : ex.Color.fromHex('#CD853F'),
      initiative: options.initiative,
      allegiance: options.allegiance,
      health: 15,
      maxHealth: 15,
      controlType: options.controlType || (options.allegiance === Allegiance.Player ?
        ControlType.Manual : ControlType.Automatic),
      moveRange: 2,
      attackRange: 1,
      abilities: ['bark_push', 'pack_instinct'],
      grid: options.grid
    });

    // Set the emoji for this unit type
    this.setEmoji("🐕"); // Dog emoji
  }

  public decideAction(): Action | null {
    // If manual control and player unit, delegate to player input
    if (this.controlType === ControlType.Manual && this instanceof PlayerUnit) {
      return super.decideAction();
    }

    // Check for enemies to attack directly
    const attackTargets = this.findTargetsInRange(this.attackRange);
    if (attackTargets.length > 0) {
      return this.createAttackAction(attackTargets[0]);
    }

    // Check for multiple enemies in range to use bark ability (2-tile radius)
    const barkTargets = this.findTargetsInRange(2);
    if (barkTargets.length >= 2) {
      return this.createBarkAction(barkTargets);
    }

    // Try to stay near an ally (if any)
    const closestAlly = this.findClosestAlly();
    if (closestAlly) {
      // Check if we're already close to an ally
      const distanceToAlly = Math.abs(closestAlly.gridX - this.gridX) + Math.abs(closestAlly.gridY - this.gridY);
      
      if (distanceToAlly > 2) {
        // Move towards the ally if too far
        const dx = closestAlly.gridX - this.gridX;
        const dy = closestAlly.gridY - this.gridY;
        
        const moveX = this.gridX + Math.sign(dx) * Math.min(Math.abs(dx), this.moveRange);
        const moveY = this.gridY + Math.sign(dy) * Math.min(Math.abs(dy), this.moveRange);
        
        if (this.grid.isValidMove(moveX, moveY)) {
          return this.createMoveAction(moveX, moveY);
        }
      }
    }

    // If no ally to follow or already close to one, move towards enemies
    const closestEnemy = this.findClosestEnemy();
    if (closestEnemy) {
      const dx = closestEnemy.gridX - this.gridX;
      const dy = closestEnemy.gridY - this.gridY;
      
      const moveX = this.gridX + Math.sign(dx) * Math.min(Math.abs(dx), this.moveRange);
      const moveY = this.gridY + Math.sign(dy) * Math.min(Math.abs(dy), this.moveRange);
      
      if (this.grid.isValidMove(moveX, moveY)) {
        return this.createMoveAction(moveX, moveY);
      }
    }

    // If no clear move, try random movement
    const directions = [
      { x: 0, y: -1 }, // Up
      { x: 1, y: 0 },  // Right
      { x: 0, y: 1 },  // Down
      { x: -1, y: 0 }  // Left
    ];
    
    // Shuffle directions for randomness
    for (let i = directions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [directions[i], directions[j]] = [directions[j], directions[i]];
    }
    
    for (const dir of directions) {
      const newX = this.gridX + dir.x;
      const newY = this.gridY + dir.y;
      
      if (this.grid.isValidMove(newX, newY)) {
        return this.createMoveAction(newX, newY);
      }
    }

    // If no valid moves, wait
    return this.createWaitAction();
  }

  // Find closest ally
  private findClosestAlly(): Actor | null {
    const allies = this.grid.getActorsByAllegiance(this.allegiance);

    if (allies.length <= 1) return null; // No allies other than self

    // Sort by distance
    const otherAllies = allies.filter(a => a !== this);
    otherAllies.sort((a, b) => {
      const distA = Math.abs(a.gridX - this.gridX) + Math.abs(a.gridY - this.gridY);
      const distB = Math.abs(b.gridX - this.gridX) + Math.abs(b.gridY - this.gridY);
      return distA - distB;
    });

    return otherAllies[0];
  }

  // Create a bark action to push enemies away
  private createBarkAction(enemies: Actor[]): Action {
    return {
      type: ActionType.Special,
      actor: this,
      execute: () => {
        // Get background color based on allegiance
        const dogBg = this.allegiance === Allegiance.Player ? 'rgba(0, 0, 255, 0.2)' : 'rgba(255, 0, 0, 0.2)';

        // Console log for bark action with emoji and colored background
        console.log(`%c ${this.emoji} ${this.name} %cbarks loudly at ${enemies.length} enemies! 🔊`,
          `font-weight: bold; background: ${dogBg}; border-radius: 3px; padding: 2px 4px;`,
          'font-weight: normal; color: black');

        // For each enemy in range, try to push them away
        for (const enemy of enemies) {
          // Determine push direction (away from dog)
          const dx = enemy.gridX - this.gridX;
          const dy = enemy.gridY - this.gridY;

          // Calculate push position
          const pushX = enemy.gridX + Math.sign(dx);
          const pushY = enemy.gridY + Math.sign(dy);

          // Get enemy background color
          const enemyBg = enemy.allegiance === Allegiance.Player ? 'rgba(0, 0, 255, 0.2)' : 'rgba(255, 0, 0, 0.2)';
          const enemyUnit = enemy as Unit;

          // Check if push position is valid
          if (this.grid.isValidMove(pushX, pushY)) {
            // Console log each enemy push with emojis and colored backgrounds
            console.log(`%c ${this.emoji} ${this.name} %cpushes %c ${enemyUnit.emoji} ${enemy.name} %cfrom (${enemy.gridX},${enemy.gridY}) to (${pushX},${pushY}) 💨`,
              `font-weight: bold; background: ${dogBg}; border-radius: 3px; padding: 2px 4px;`,
              'font-weight: normal; color: black',
              `font-weight: bold; background: ${enemyBg}; border-radius: 3px; padding: 2px 4px;`,
              'font-weight: normal; color: black');

            // Move the enemy to that position
            enemy.moveToGrid(pushX, pushY);
          } else {
            console.log(`%c ${this.emoji} ${this.name} %ctried to push %c ${enemyUnit.emoji} ${enemy.name} %cbut they hit an obstacle 🧱`,
              `font-weight: bold; background: ${dogBg}; border-radius: 3px; padding: 2px 4px;`,
              'font-weight: normal; color: black',
              `font-weight: bold; background: ${enemyBg}; border-radius: 3px; padding: 2px 4px;`,
              'font-weight: normal; color: black');
          }
        }

        this.actionPoints--;
      }
    };
  }
}